import { Component, forwardRef, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import {
    ClassicEditor,
    Essentials,
    Paragraph,
    Bold,
    Italic,
    Link,
    List,
    Heading,
    Image,
    ImageToolbar,
    ImageUpload,
    MediaEmbed,
    BlockQuote,
    SourceEditing,
    HtmlEmbed,
    GeneralHtmlSupport
} from 'ckeditor5';

// Resim yükleme adaptörü sınıfı
class MyUploadAdapter {
    private loader;

    constructor(loader: any) {
        this.loader = loader;
    }

    upload(): Promise<any> {
        return this.loader.file.then((file: File) => {
            return new Promise((resolve) => {
                // Burada normalde bir API'ye dosya yüklersiniz
                // Şimdilik base64 olarak dönüştürüp kullanacağız
                const reader = new FileReader();
                reader.onload = () => {
                    resolve({
                        default: reader.result
                    });
                };
                reader.readAsDataURL(file);
            });
        });
    }

    abort(): void {
        // Yükleme iptal edildiğinde yapılacak işlemler
    }
}

// CKEditor plugin fonksiyonu
function MyCustomUploadAdapterPlugin(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
        return new MyUploadAdapter(loader);
    };
}

@Component({
    selector: 'app-rich-text-editor',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, CKEditorModule],
    templateUrl: './rich-text-editor.component.html',
    styleUrls: ['./rich-text-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RichTextEditorComponent),
            multi: true
        }
    ]
})
export class RichTextEditorComponent implements OnInit, OnChanges, ControlValueAccessor {
    @Input() placeholder: string = 'İçeriğinizi buraya yazın...';
    @Input() height: string = '300px';

    public Editor = ClassicEditor;
    public editorData: string = '';
    public editorConfig: any;
    disabled: boolean = false;

    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnInit(): void {
        this.initEditorConfig();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['height'] && !changes['height'].firstChange) {
            // Yükseklik değiştiğinde editör yapılandırmasını güncelle
            this.initEditorConfig();
        }
    }

    private initEditorConfig(): void {
        this.editorConfig = {
            licenseKey: 'GPL',
            plugins: [
                Essentials,
                Paragraph,
                Bold,
                Italic,
                Link,
                List,
                Heading,
                Image,
                ImageToolbar,
                ImageUpload,
                MediaEmbed,
                BlockQuote,
                SourceEditing,
                HtmlEmbed,
                GeneralHtmlSupport,
                MyCustomUploadAdapterPlugin
            ],
            toolbar: {
                items: [
                    'heading',
                    '|',
                    'bold',
                    'italic',
                    'link',
                    'bulletedList',
                    'numberedList',
                    '|',
                    'imageUpload',
                    'mediaEmbed',
                    '|',
                    'blockQuote',
                    'undo',
                    'redo',
                    '|',
                    'sourceEditing'
                ]
            },
            heading: {
                options: [
                    { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                    { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' }
                ]
            },
            placeholder: this.placeholder,
            image: {
                toolbar: ['imageTextAlternative', 'imageStyle:full', 'imageStyle:side'],
                upload: {
                    types: ['jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff']
                }
            },
            mediaEmbed: {
                previewsInData: true
            },
            extraPlugins: [MyCustomUploadAdapterPlugin],
            ui: {
                viewportOffset: { top: 10, right: 10, bottom: 10, left: 10 }
            },
            htmlSupport: {
                allow: [
                    {
                        name: /.*/,
                        attributes: true,
                        classes: true,
                        styles: true
                    }
                ]
            },
            htmlEmbed: {
                showPreviews: true
            }
        };
    }

    // ControlValueAccessor arayüzü metodları
    writeValue(value: string): void {
        this.editorData = value || '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // CKEditor değişiklik olayı
    onEditorChange({ editor }: any): void {
        const data = editor.getData();
        this.onChange(data);
        this.onTouched();
    }
}
